import isNil from './isNil';

type FormatValueOrDefaultProps<T = unknown, R = unknown> = {
  /**
   * 원본 값
   */
  origin: T | null | undefined;
  /**
   * 대체 텍스트
   * 원본 값이 존재하지 않을 경우 대체 텍스트를 반환합니다.
   * @default '-'
   */
  emptyText?: string;
  /**
   * 값이 있을 때, 해당 값을 포맷팅하는 함수
   * 함수를 정의하지 않을 경우 origin 값을 반환합니다.
   * @param value 원본 값
   * @returns 포맷팅된 값
   */
  formatter?: (value: T) => R;
};

const DEFAULT_EMPTY_TEXT = '-';

function formatValueOrDefault<T>(props: {
  origin: T | null | undefined;
  emptyText?: string;
  formatter?: undefined;
}): string | T;

function formatValueOrDefault<T, R>(props: {
  formatter: (value: T) => R;
  origin: T | null | undefined;
  emptyText?: string;
}): string | R;

/**
 * 전달된 값이 null, undefined, '' 인 경우 대체 텍스트를 반환합니다.
 * @param props
 * @param props.origin 원본 값
 * @param props.emptyText 대체 텍스트
 * @param props.formatter 값을 포맷팅하는 함수
 * @returns 포맷팅된 값
 * @example
 * const renderedValue = formatValueOrDefault({
 *   origin: 'test',
 *   formatter: value => <Status status={getSubnetStatusType(value)}>{value}</Status>,
 * });
 */
function formatValueOrDefault<T = unknown, R = unknown>(
  props: FormatValueOrDefaultProps<T, R>,
): string | T | R {
  const { origin, formatter, emptyText = DEFAULT_EMPTY_TEXT } = props;

  if (isNil(origin)) {
    return emptyText;
  }

  if (typeof origin === 'string' && origin.trim() === '') {
    return emptyText;
  }

  // 빈 객체 {}, 빈 배열 [] 인 경우 대응
  if (
    typeof origin === 'object' &&
    !(origin instanceof Date) &&
    Object.keys(origin as object).length === 0
  ) {
    return emptyText;
  }

  return formatter ? formatter(origin) : origin;
}

export default formatValueOrDefault;
